import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, NgZone, Signal, WritableSignal, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, catchError, interval, map, takeWhile, throwError } from 'rxjs';
import { environment } from '@env';
import { FormArray, FormGroup } from '@angular/forms';
import { isArray, isEmpty } from 'underscore';
import Swal from 'sweetalert2';
import { StorageService } from './storage.service';
import { EventService } from './event.service';
import { IResponse } from '../interface/baseApiResponse';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);
  private event = inject(EventService);
  private zone = inject(NgZone);
  private API_URL: WritableSignal<string> = signal(environment.BASE_API_PATH);
  httpOptions = signal({
    headers: new HttpHeaders({
      Accept: 'multipart/form-data',
      "Cache-Control": "max-age=84600, public"
    })
  });

  private isLoggedIn: Signal<boolean> = signal((typeof this.storage.getDataField('token') === 'string'));

  private router = inject(Router);

  private timerDuration = 60; // 60 seconds
  private timerEndTime: number | null = null;
  private timerSubscription: Subscription | null = null;  
  private timerSubject = new BehaviorSubject<number>(this.timerDuration);
  timer$ = this.timerSubject.asObservable();

  timerStart = signal<boolean>(false);

  startTimer() {
    const _timerStart = this.timerStart();
    !_timerStart && this.timerStart.set(true);
    if (this.timerEndTime) {
      const remainingTime = Math.floor((this.timerEndTime - Date.now()) / 1000);
      if (remainingTime > 0) {
        this.runTimer(remainingTime);
        return;
      }
    }
    
    this.timerEndTime = Date.now() + this.timerDuration * 1000;
    this.runTimer(this.timerDuration);
  }

  private runTimer(duration: number) {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = interval(1000).pipe(
      map(t => duration - t),
      takeWhile(time => time >= 0)
    ).subscribe(time => {
      this.timerSubject.next(time);
      if (time === 0) this.timerEndTime = null;
    });
  }

  resetTimer() {
    this.timerEndTime = null;
    this.startTimer();
  }

  constructor() { }

  public igolfRequest(action: string, body: { id_course: string }, hasLoader: boolean = false,
    e: { apikey: string, secretkey: string } = { apikey: environment.IGOLF.API_KEY, secretkey: environment.IGOLF.SECRET_KEY }): Promise<Observable<any>> {
    const base = environment.IGOLF_BASE_PATH;

    const pad2 = (e: number) => {
      return (e < 10 ? "0" : "") + e;
    }
    const createTimestamp = () => {
      const o = new Date();
      const a = o.getTimezoneOffset()
      return String(o.getFullYear()).slice(-2) +
        pad2(o.getMonth() + 1) +
        pad2(o.getDate()) +
        pad2(o.getHours()) +
        pad2(o.getMinutes()) +
        pad2(o.getSeconds()) +
        (a > 0 ? "-" : "-") +
        pad2(Math.floor(Math.abs(a) / 60)) +
        pad2(Math.abs(a) % 60);
    }

    const encode64 = (e: any) => {
      return btoa(
        new Uint8Array(e).reduce((e, t) => {
          return e + String.fromCharCode(t);
        }, "")
      );
    }

    const getSignature = (action: string, e: { apikey: string, secretkey: string }) => {
      return (crypto || (crypto = window.crypto || (window as any).msCrypto),
        crypto.subtle.importKey("raw", new TextEncoder().encode(e.secretkey), {
          name: "HMAC",
          hash: {
            name: "SHA-256"
          }
        }, !1, ["sign", "verify"]).then((r) => {
          const API_VERSION = environment.IGOLF.API_VERSION,
            SIG_VERSION = environment.IGOLF.SIG_VERSION,
            APP_API_KEY = e.apikey,
            SIG_METHOD = environment.IGOLF.SIG_METHOD,
            RESPONSE_FORMAT = environment.IGOLF.RESPONSE_FORMAT,
            timeStamp = createTimestamp();
          const i = `${action}/${APP_API_KEY}/${API_VERSION}/${SIG_VERSION}/${SIG_METHOD}/${timeStamp}/${RESPONSE_FORMAT}`
          return crypto.subtle.sign({
            name: "HMAC",
            hash: {
              name: "SHA-256"
            }
          }, r, new TextEncoder().encode(i));
        }).catch((e) => {
          console.error(e);
        }).then((t) => {
          return encode64(t).replace(/\//g, "_").replace(/\+/g, "-").replace(/=/g, "")
        })
      )
    }

    const arr: Array<any> = [];
    return getSignature(action, e).then((signature) => {
      const object: any = {
        action: action,
        apiKey: e.apikey,
        appVersion: '1.1',
        apiVersion: '2.0',
        signatureVersion: 'HmacSHA256',
        signatureMethod: signature,
        timestamp: createTimestamp(),
        responseFormat: 'JSON'
      }
      for (const key in object) {
        if (Object.hasOwnProperty.call(object, key)) {
          arr.push(object[key])
        }
      }
      const url = arr.join('/');
      const PATH = base + '/rest/action/' + url;
      return this.http.post(PATH, body).pipe(catchError(this.formatErrors));
    })


  }


  getLocal(path: string, params: HttpParams = new HttpParams(), hasLoader = true): Observable<any> {
    return this.http.get(`${path}`, { headers: this.httpOptions().headers.append('hasLoader', hasLoader + ''), params })
      .pipe(catchError(this.formatErrors));
  }

  postLocal(path: string, body: any, hasLoader = true): Observable<any> {
    return this.http.post(`${path}`, body).pipe(catchError(this.formatErrors));
  }

  public async logout(): Promise<void> {
    this.storage.clearUser();
    this.event.isLoggedIn.set(false);
    this.event.user.set(false);
    // this.router.navigate(['/login'])
  }

  get(path: string, params: object = {}): Observable<IResponse | any> {
    return this.http.get(`${this.API_URL()}${path}`, { headers: this.httpOptions().headers, params: { ...params } }).pipe(catchError(this.formatErrors))
  }

  post(path: any, body: object = {}, hasLoader = true): Observable<IResponse | any> { 
    return this.http.post(`${this.API_URL()}${path}`, body, { headers: this.httpOptions().headers.append('hasLoader', hasLoader + '') }).pipe(catchError(this.formatErrors));
  }

  patch(path: any, body: object = {}) {
    return this.http.patch(`${this.API_URL()}${path}`, body, { headers: this.httpOptions().headers }).pipe(catchError(this.formatErrors));
  }

  put(path: any, body: object = {}) {
    return this.http.put(`${this.API_URL()}${path}`, body, { headers: this.httpOptions().headers }).pipe(catchError(this.formatErrors));
  }

  delete(path: string, params: HttpParams = new HttpParams()) {
    return this.http.delete(`${this.API_URL()}${path}`, { headers: this.httpOptions().headers, params }).pipe(catchError(this.formatErrors));
  }

  private formatErrors(error: any) {
    return throwError(() => new Error(error?.error?.message));
  }

  alert(message: string, type: 'warning' | 'success' | 'error' | 'question', duration?: number) {
    return Swal.fire({
      title: message,
      icon: type,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: duration ? duration : 5000,
      timerProgressBar: true,
    });
  }

  // SHOW ALERT AS A POPUP WHEN REQUIRED
  alertModal(message: string, type: 'warning' | 'success' | 'error' | 'question' | 'info', CancelButton = false) {
    return Swal.fire({
      text: message,
      icon: type,
      showConfirmButton: true,
      showCancelButton: CancelButton,
      confirmButtonText: 'Ok',
    });
  }
  confirmModal(message: string, type: any, CancelButton = false, isIcon?: boolean, confirmText?: string, cancelText?: string, customClass?: string) {
    return Swal.fire({
      text: message,
      icon: isIcon ? type : null,
      showConfirmButton: true,
      showCancelButton: CancelButton,
      confirmButtonText: confirmText ? confirmText : 'Ok',
      cancelButtonText: cancelText ? cancelText : 'Cancel'
    });
  }

  beforeSubmit(form: FormGroup): Promise<void> {
    return new Promise((resolve) => {
      Object.keys(form.value).forEach((x) => {
        if (typeof (x) === 'string' && typeof (form.controls[x].value) === 'string' && !isEmpty(form.controls[x].value)) {
          form.controls[x].setValue(form.controls[x].value.trim());
        } else if (isArray(form.controls[x].value)) {
          for (const iterator of (form.controls[x] as FormArray).controls) {
            this.beforeSubmit(iterator as FormGroup)
          }
        }
        resolve(form.value);
      });
    });
  }

  searchNearby(): void {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      return;
    }
  
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const str = `?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}`
        this.router.navigateByUrl(`/search${str}`);
      },
      (error) => {
        // Redirect to the default region (USA) if location services are disabled or unavailable
        this.router.navigate([environment.defaultRegionUrl])
        console.error("Error getting location: ", error);
      },
      { enableHighAccuracy: true }
    );
  }
  scrollTo(top: number): void {
    this.zone.runOutsideAngular(() => {
      if (typeof (window) !== 'undefined') {
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    })
  }
}
